<template>
  <!--
    Deliberately NOT a modal. The two surfaces that crop are the profile sheet
    and the shopping-list editor, and both are already overlays — opening a
    dialog on top of a dialog stacks two focus traps and two dismiss handlers
    on the same Escape key. This renders in place instead, where the photo it
    is replacing was.
  -->
  <div v-if="modelValue" class="cropper">
    <p class="cropper-title label-caps">{{ t("images.crop_title") }}</p>
    <!-- The frame is the crop. The image moves behind it, which is why the
         mask sits on top and the whole thing is one drag surface. -->
      <div
        ref="frame"
        class="cropper-frame"
        :class="{ 'cropper-frame--round': round }"
        :style="{ aspectRatio: String(aspect) }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @wheel.prevent="onWheel"
      >
        <img
          v-if="src"
          :src="src"
          class="cropper-img"
          :style="imgStyle"
          alt=""
          draggable="false"
          @load="onImageLoad"
        />
        <!-- The mask takes its shape from the frame via border-radius: inherit,
             so it needs no round modifier of its own. -->
        <div class="cropper-mask" aria-hidden="true"></div>
      </div>

      <label class="cropper-zoom">
        <span class="label-caps">{{ t("images.crop_zoom") }}</span>
        <input
          v-model.number="zoom"
          type="range"
          :min="1"
          :max="MAX_ZOOM"
          :step="0.01"
          :aria-label="t('images.crop_zoom')"
        />
      </label>

    <p class="cropper-hint">{{ t("images.crop_hint") }}</p>

    <div class="cropper-actions">
      <AppButton variant="outline" size="sm" @click="cancel">{{ t("common.cancel") }}</AppButton>
      <AppButton variant="primary" size="sm" :disabled="!ready" @click="confirm">
        {{ t("images.crop_confirm") }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { CropRect } from "../../services/imageCapture"
import AppButton from "./AppButton.vue"

/**
 * Pan-and-zoom cropper, rendered inline by ImagePicker — never as a dialog.
 * Both surfaces that use it sit inside an overlay already, and a modal inside
 * a modal means two focus traps and two Escape handlers fighting.
 *
 * The web file picker hands back whatever the camera roll holds, and the
 * centre cut that `prepareImage` falls back to decapitates about half of all
 * portrait photos. Native doesn't need this — Capacitor opens the OS editor —
 * so this only ever runs on web.
 *
 * State is two numbers: `zoom` (1 = the image exactly covers the frame) and an
 * offset in frame pixels. Everything else is derived, and the crop rect is
 * computed from them once, on confirm.
 */
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    /** Data URL of the picked, untouched image. */
    src: string | null
    /** width/height of the crop window. */
    aspect?: number
    /** Circular mask (avatars). The output is still square. */
    round?: boolean
  }>(),
  { aspect: 1, round: false },
)

const emit = defineEmits<{
  "update:modelValue": [open: boolean]
  crop: [rect: CropRect]
  cancel: []
}>()

const { t } = useI18n()

const MAX_ZOOM = 4

const frame = ref<HTMLElement | null>(null)
const natural = ref({ w: 0, h: 0 })
const zoom = ref(1)
const offset = ref({ x: 0, y: 0 })
const dragging = ref(false)
const dragStart = ref({ x: 0, y: 0, ox: 0, oy: 0 })

const ready = computed(() => natural.value.w > 0 && natural.value.h > 0)

// Reset whenever a new image arrives, so the previous photo's framing doesn't
// carry over onto it.
watch(
  () => props.src,
  () => {
    natural.value = { w: 0, h: 0 }
    zoom.value = 1
    offset.value = { x: 0, y: 0 }
  },
)

function onImageLoad(event: Event) {
  const img = event.target as HTMLImageElement
  natural.value = { w: img.naturalWidth, h: img.naturalHeight }
  zoom.value = 1
  offset.value = { x: 0, y: 0 }
}

function frameSize() {
  const el = frame.value
  if (!el) return { w: 0, h: 0 }
  return { w: el.clientWidth, h: el.clientHeight }
}

/** Scale at which the image exactly covers the frame — the zoom floor. */
function coverScale() {
  const { w, h } = frameSize()
  if (!ready.value || w === 0 || h === 0) return 1
  return Math.max(w / natural.value.w, h / natural.value.h)
}

const scale = computed(() => coverScale() * zoom.value)

/** Offset limits that keep the frame fully covered — no empty corners. */
function clampOffset(x: number, y: number) {
  const { w, h } = frameSize()
  const dw = natural.value.w * scale.value
  const dh = natural.value.h * scale.value
  const maxX = Math.max(0, (dw - w) / 2)
  const maxY = Math.max(0, (dh - h) / 2)
  return {
    x: Math.max(-maxX, Math.min(maxX, x)),
    y: Math.max(-maxY, Math.min(maxY, y)),
  }
}

// Re-clamp on zoom out, or the image can end up parked off-centre with a gap.
watch(zoom, () => {
  offset.value = clampOffset(offset.value.x, offset.value.y)
})

const imgStyle = computed(() => {
  if (!ready.value) return { visibility: "hidden" as const }
  return {
    width: `${natural.value.w * scale.value}px`,
    height: `${natural.value.h * scale.value}px`,
    transform: `translate(-50%, -50%) translate(${offset.value.x}px, ${offset.value.y}px)`,
  }
})

function onPointerDown(event: PointerEvent) {
  if (!ready.value) return
  dragging.value = true
  dragStart.value = { x: event.clientX, y: event.clientY, ox: offset.value.x, oy: offset.value.y }
  ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  if (!dragging.value) return
  const dx = event.clientX - dragStart.value.x
  const dy = event.clientY - dragStart.value.y
  offset.value = clampOffset(dragStart.value.ox + dx, dragStart.value.oy + dy)
}

function onPointerUp(event: PointerEvent) {
  if (!dragging.value) return
  dragging.value = false
  const el = event.currentTarget as HTMLElement
  if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId)
}

function onWheel(event: WheelEvent) {
  if (!ready.value) return
  const next = zoom.value * (event.deltaY < 0 ? 1.1 : 1 / 1.1)
  zoom.value = Math.max(1, Math.min(MAX_ZOOM, next))
}

/**
 * Frame → source pixels. The frame's top-left corner in image coordinates is
 * how far the displayed image has been pushed past it, divided back out by the
 * scale it is displayed at.
 */
function toCropRect(): CropRect {
  const { w, h } = frameSize()
  const s = scale.value
  const dw = natural.value.w * s
  const dh = natural.value.h * s
  const left = w / 2 + offset.value.x - dw / 2
  const top = h / 2 + offset.value.y - dh / 2
  return {
    sx: Math.max(0, -left / s),
    sy: Math.max(0, -top / s),
    sw: Math.min(natural.value.w, w / s),
    sh: Math.min(natural.value.h, h / s),
  }
}

function confirm() {
  if (!ready.value) return
  emit("crop", toCropRect())
  emit("update:modelValue", false)
}

function cancel() {
  emit("cancel")
  emit("update:modelValue", false)
}
</script>
